import {
  CsvFileUploadImportConfig,
  FileUploadImportConfig,
  LocalImportConfig,
  WebhookImportConfig,
} from '@oneschema/react';
import isString from 'lodash/isString';
import { Company } from '~/types/company';
import { CostCenterFilterValue } from '~/types/cost-center';
import { Counterpart } from '~/types/counterparts';
import { DocumentAttachment } from '~/types/document-attachment';
import { Flow, FlowPaymentStatus, PaymentMethod } from '~/types/flow';
import { BookkeepingEntry } from '~/types/general-bookkeeping';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { Money } from '~/types/money';
import { SortInfo } from '~/utils/jsonapi-query';
import { Account } from './account';
import { Category, CategoryFilterValue, Subcategory } from './category';
import { DocumentAllocation, DocumentAllocationUpdatePayload } from './document-allocation';
import { DocumentSectional } from './document-sectional';
import { Recurrence } from './recurrence';
import { TransactionFlowDirection } from './transaction';
import { isDefined } from './typeGuards';
import { BillTypeFilter, VATRegistration } from './vat-bookkeeping';

export const DocumentResourceType = 'document';

export const DocumentQueryKeys = {
  all: ['Documents'],
  hasDocumentsAll: ['HasDocuments'],
  hasDocuments: (companyId: string) => ['HasDocuments', { companyId }],
  companyList: (companyId: string) => ['Documents', { companyId }],
  allCompanyInfinityList: (companyId: string) => ['Documents', { companyId }, 'Infinite'],
  companyInfinityList: (
    companyId: string,
    filters: DocumentsQueryFilters,
    sorting: SortInfo<DocumentsQuerySorting>,
  ) => ['Documents', { companyId }, 'Infinite', filters, sorting],
  single: (id: string) => ['Documents', id],
  singleWithVatRegistration: (id: string) => ['Documents', id, 'VatRegistration'],
  counterparts: (companyId: string, searchText: string) => ['Documents', 'Counterparts', companyId, searchText],
  metadata: (companyId: string, filters: DocumentsQueryFilters) => ['Documents', { companyId }, 'Metadata', filters],
  lastDocumentsNumber: (
    companyId: string,
    documentType?: DocumentType,
    direction?: DocumentDirection,
    creationDate?: string,
  ) => ['Documents', { companyId, documentType, direction, creationDate }, 'LastDocumentIssued'],
  documentNumberExists: (
    companyId: string,
    documentNumber?: string,
    documentType?: DocumentType,
    excludeId?: Document['id'],
    creationDate?: string,
  ) => ['Documents', { companyId, documentNumber, documentType, creationDate, excludeId }, 'DocumentNumberExists'],
  latestDocumentCreationDate: (companyId: string, documentType: DocumentType, creationDate?: Date) => [
    'Documents',
    { companyId, documentType, creationDate },
    'LatestDocumentCreationDate',
  ],
  deliveryNotesMultipleAsidePreview: (
    companyId: string,
    filters: DocumentsQueryFilters,
    sorting: SortInfo<DocumentsQuerySorting>,
  ) => ['Documents', { companyId }, 'DeliveryNotesMultipleAsidePreview', filters, sorting],
  lastProgressiveBySectional: (
    companyId: string,
    documentType: DocumentType,
    direction: DocumentDirection,
    sectionalId: string | null,
    creationDate: string,
  ) => ['Documents', { companyId, documentType, direction, sectionalId, creationDate }, 'LastProgressiveBySectional'],
  documentSummary: (date: [Date, Date]) => ['DocumentsSummary', date],
  billsWithoutVATRegistration: (companyId: string, filters: BillsWithoutVATRegistrationsFilter) => [
    'Documents',
    companyId,
    'BillsWithoutVATRegistration',
    filters,
  ],
  billsWithoutVATRegistrationSuggestions: (companyId: string, filters: BillsWithoutVATRegistrationsFilter) => [
    'Documents',
    companyId,
    'BillsWithoutVATRegistrationSuggestions',
    filters,
  ],
  downloadUrl: (id: string) => ['Documents', id, 'DownloadUrl'],
};

export const DocumentImportQueryKeys = {
  importTemplates: (companyId: string) => ['DocumentImport', companyId, 'ImportTemplates'],
  importConfig: (companyId: string, templateId: string) => ['DocumentImport', companyId, 'ImportConfig', templateId],
};

export interface DocumentsQueryFilters {
  totalAmount?: [number | null, number | null, (TransactionFlowDirection | null)?] | null;
  creationDate?: [string | null, string | null];
  deliveryDate?: [string | null, string | null];
  acceptanceDate?: [string | null, string | null];
  documentTypes?: DocumentType[];
  currencies?: string[];
  paymentStatus?: DocumentPaymentStatus | null;
  direction?: DocumentDirection | null;
  rawDirection?: DocumentDirection | null;
  creators?: DocumentCreator[];
  search?: string;
  searchBills?: string;
  category?: CategoryFilterValue | null;
  costCenter?: CostCenterFilterValue;
  withNumber?: boolean;
  number?: string;
  numberIcase?: string;
  status?: DocumentStatus[];
  counterpart?: string[];
  counterpartCountry?: string;
  ids?: Document['id'][];
  isElectronic?: boolean;
  excludeIds?: Document['id'][];
  flowCount?: {
    operator: '=' | '<' | '<=' | '>=' | '>';
    value: number;
  };
  flowHasPaymentMethod?: PaymentMethod[];
  flowHasPaymentStatus?: FlowPaymentStatus | null;
  flowHasExpectedPaymentDate?: [string | null, string | null];
  includeHidden?: boolean;
  hasVATRegistrations?: boolean;
  detectionDatetime?: [string | null, string | null];
  hasBookkeepingTransactions?: boolean;
  hasOpenBalance?: boolean;
  hasWithholdingFlow?: boolean;
  nonWithholdingFlowPaymentDate?: [string | null, string | null];
  withholdingFlowPaymentDate?: [string | null, string | null];
  withholdingFlowPaymentStatus?: FlowPaymentStatus | null;
  deviceIds?: string[];
  billType?: BillTypeFilter | null;
}

export enum DocumentType {
  Invoice = 'INVOICE',
  SelfInvoice = 'SELF_INVOICE',
  Parcel = 'PARCEL',
  CreditNote = 'CREDIT_NOTE',
  DebitNote = 'DEBIT_NOTE',
  Bill = 'BILL',
  Receipt = 'RECEIPT',
  DeliveryNote = 'DELIVERY_NOTE',
  Other = 'OTHER',
  Quote = 'QUOTE',
  Payroll = 'PAYROLL',
}

export type FinalizableDocumentType =
  | DocumentType.Invoice
  | DocumentType.SelfInvoice
  | DocumentType.Parcel
  | DocumentType.CreditNote
  | DocumentType.DebitNote
  | DocumentType.DeliveryNote
  | DocumentType.Quote;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const AllDocumentTypes = [
  DocumentType.Invoice,
  DocumentType.CreditNote,
  DocumentType.DebitNote,
  DocumentType.Parcel,
  DocumentType.SelfInvoice,
  DocumentType.Bill,
  DocumentType.Receipt,
  DocumentType.DeliveryNote,
  DocumentType.Other,
];

export const IssuableDocumentTypes = [
  DocumentType.Invoice,
  DocumentType.CreditNote,
  DocumentType.DebitNote,
  DocumentType.Parcel,
  DocumentType.SelfInvoice,
  DocumentType.Bill,
  DocumentType.Receipt,
  DocumentType.Other,
];

export enum EInvoiceType {
  Fattura = 'TD01',
  AccontoFattura = 'TD02',
  NotaDiCredito = 'TD04',
  NotaDiDebito = 'TD05',
  Parcella = 'TD06',
  AutofatturaReverseCharge = 'TD16',
  AutofatturaServiziEsteri = 'TD17',
  AutofatturaBeniEU = 'TD18',
  AutofatturaBeniEsteri = 'TD19',
  FatturaDifferita = 'TD24',
  AutofatturaCessioneDiBeniAmmortizzabili = 'TD26',
  AutofatturaPerAutoconsumo = 'TD27',
  CommunicationOmittedIrregularInvoice = 'TD29',
}

export enum DocumentPaymentStatus {
  Paid = 'PAID',
  PartiallyPaid = 'PARTIALLY_PAID',
  ToPay = 'TO_PAY',
}

export type DocumentsQuerySorting =
  | 'creationDate'
  | 'searchDate'
  | 'grossAmount'
  | 'number'
  | 'deliveryDate'
  | 'progressive';

export enum DocumentDirection {
  issued = 'ISSUED',
  received = 'RECEIVED',
}

export enum DocumentFormat {
  FPA = 'FPA12',
  FPR = 'FPR12',
  FSM = 'FSM10',
}

export enum DocumentStatus {
  Draft = 'DRAFT',
  Created = 'CREATED',
  Sent = 'SENT',
  Discarded = 'DISCARDED',
  Delivered = 'DELIVERED',
  Not_Delivered = 'NOT_DELIVERED',
  Refused = 'REFUSED',
}

export enum DocumentQuickFilter {
  Draft = 'DRAFT',
  Finalized = 'FINALIZED',
  Error = 'ERROR',
  ToCacheOut = 'TO_CACHE_OUT',
}

export enum DocumentSource {
  User = 'USER',
  SDICoop = 'SDICOOP',
  Entratel = 'ENTRATEL',
}

export enum DocumentCreator {
  Builder = 'BUILDER',
  Import = 'IMPORT',
  IntegrationApi = 'INTEGRATION_API',
  ThirdParty = 'THIRD_PARTY',
  Email = 'EMAIL',
}

export interface Document {
  category?: Category;
  allocations?: DocumentAllocation[];
  company: Company;
  counterpart?: Counterpart;
  counterpartName: string | null;
  creationDate: string;
  createdAt: string;
  creator: DocumentCreator;
  deletable: boolean;
  deliveryDate: string | null;
  acceptanceDate: string | null;
  deliveryStatus: string | null;
  direction: DocumentDirection;
  documentType: DocumentType;
  duplicable: boolean;
  editable: boolean;
  format: DocumentFormat | null;
  isEInvoice: boolean;
  flows?: Flow[];
  grossAmount: Money;
  hasAttachment: boolean;
  hiddenAt: Date | null;
  id: string;
  isFromRecurrence: boolean;
  isInflow: boolean;
  emailSender?: string;
  subjectToReverseCharge: boolean;
  detectionDatetime: string | null;
  lastEmail?: string | null;
  notes: string | null;
  number: string | null;
  paymentStatus: DocumentPaymentStatus | null;
  progressive: string | null;
  recurrence: Recurrence | null;
  sectional: DocumentSectional | null;
  source: DocumentSource;
  status: DocumentStatus;
  subcategory?: Subcategory;
  vatAmount: Money;
  vatCollection: DocumentVATCollection | null;
  withholdingTax: { amount: string; currency: string } | null;
  withholdingTaxDetails?: WithholdingTaxDetailData[] | null;
  related: Document[];
  referencedBy: Document[];
  bookkeepingEntries: BookkeepingEntry[];
}

export interface DocumentWithAttachments extends Document {
  attachments: DocumentAttachment[];
  flows: Flow[];
  vatRegistrations?: VATRegistration[];
}

export interface CreateDocumentForm {
  document: {
    documentType: DocumentType;
    creationDate: Date;
    direction: DocumentDirection;
    number: string;
    counterpartName: string;
    totalAmount: string;
    vatAmount: string;
    currency: string;
    counterpart: Counterpart['id'] | null;
    category: Category['id'] | null;
    subcategory: Subcategory['id'] | null;
  };
  flowsShared: {
    payeeName?: string | null;
    paymentAccount: string | null;
    account: Account['id'] | null;
    paymentMethod: PaymentMethod | null;
  };
  flows: {
    paymentStatus: FlowPaymentStatus;
    totalAmount: string;
    expectedPaymentDate: Date;
    notes?: string;
  }[];
  withholdingTax:
    | {
        type: WithholdingTaxType;
        motivation: string;
        percentage: string;
        taxableAmount: string;
      }[]
    | null;
}

export interface Total {
  currency: string;
  positive: Money;
  negative: Money;
}

export interface DocumentsMeta {
  page: MetaDataCursorPage;
}

export interface WithholdingTaxDetailData {
  type: string;
  motivation: string;
  percentage: string;
  taxableAmount: Money;
  amount?: Money;
}

export interface InvoiceCreationData {
  isInflow: boolean;
  documentType: DocumentType;
  creationDate: string;
  number: string;
  counterpartName: string;
  grossAmount: Money;
  vatAmount: Money;
  vatAmountCompensation: Money;
  counterpart: { id: Counterpart['id'] } | null;
  company: { id: Company['id'] };
  category: { id: Category['id'] } | null;
  subcategory: { id: Subcategory['id'] } | null;
  withholdingTaxDetails?: WithholdingTaxDetailData[] | null;
}

export interface UpdateDocumentForm {
  documentType?: DocumentType;
  creationDate?: Date;
  number?: string;
  counterpartName?: string | null;
  totalAmount?: string;
  vatAmount?: string;
  currency?: string;
  counterpart?: Counterpart['id'] | null;
  category?: Category['id'] | null;
  subcategory?: Subcategory['id'] | null;
  notes?: string;
  hiddenAt?: Date | null;
  allocations?: DocumentAllocationUpdatePayload[];
}

export interface InvoiceDashboardParams {
  companyId: Company['id'];
  date: [Date, Date];
}

export interface CounterpartInfo {
  amount: Money;
  amountPercentage: number;
  counterpartId: string;
  counterpartName: string;
  counterpartIdentifier: string;
  numberOfDocuments: number;
}

interface RevenueDataPoint {
  costsAmount: Money;
  month: number;
  revenuesAmount: Money;
  year: number;
}

interface TaxDataPoint {
  month: number;
  vatCreditAmount: Money;
  vatDebitAmount: Money;
  year: number;
}

export interface DocumentsSummaryResponse {
  customers: CounterpartInfo[];
  suppliers: CounterpartInfo[];
  revenueSummary: {
    data: RevenueDataPoint[];
    totals: {
      costsAmount: Money;
      netAmount: Money;
      revenuesAmount: Money;
    };
  };
  taxesSummary: {
    data: TaxDataPoint[];
    totals: {
      netAmount: Money;
      vatCreditAmount: Money;
      vatDebitAmount: Money;
    };
  };
}

export interface DocumentBulkUpdate {
  paymentStatus?: DocumentPaymentStatus;
  categoryId?: string | null;
  subcategoryId?: string | null;
}

export enum DocumentVATCollection {
  Deferred = 'DEFERRED',
  Immediate = 'IMMEDIATE',
  Split = 'SPLIT',
}

export enum WithholdingTaxType {
  PersoneFisiche = 'RT01',
  PersoneGiuridiche = 'RT02',
  ContributoINPS = 'RT03',
  ContributoENASARCO = 'RT04',
  ContributoENPAM = 'RT05',
  AltroContributo = 'RT06',
}

export const isDocumentType = (val: unknown): val is DocumentType => {
  if (!isDefined(val)) return false;
  if (!isString(val)) return false;
  return Object.values(DocumentType)
    .map((el) => el.toString())
    .includes(val);
};

export interface BillsWithoutVATRegistrationsFilter {
  deviceId?: string;
  billType?: BillTypeFilter | null;
  creationDate: [Date | null, Date | null];
  detectionDatetime?: [Date | null, Date | null];
}

export interface BillsFiltersFields {
  search: string;
  dateRange: [string | null, string | null];
  amountRange: [number | null, number | null];
  paymentStatus: DocumentPaymentStatus | null;
  currency: string | null;
  category: CategoryFilterValue | null;
}

export interface DeliveryNotesFiltersFields {
  search: string;
  dateRange: [string | null, string | null];
  currency: string | null;
  status: DocumentStatus[];
}

export interface InvoicesFiltersFields {
  search: string;
  dateRange: [string | null, string | null];
  amountRange: [number | null, number | null];
  paymentStatus: DocumentPaymentStatus | null;
  currency: string | null;
  documentType: DocumentType[];
  category: CategoryFilterValue | null;
  costCenter: CostCenterFilterValue;
  status: DocumentStatus[];
}

export type InvoicesSortFields = 'creationDate' | 'searchDate' | 'searchAcceptanceDate' | 'grossAmount';

export interface QuotesFiltersFields {
  search: string;
  dateRange: [string | null, string | null];
  currency: string | null;
  status: DocumentStatus[];
}
export interface InvoiceImportTemplate {
  id: string;
  name: string;
  description: string;
}

export interface InvoiceImportConfig {
  baseUrl: string;
  clientId: string;
  templateKey: string;
  importConfig: LocalImportConfig | WebhookImportConfig | CsvFileUploadImportConfig | FileUploadImportConfig;
  userJwt: string;
  devMode: boolean;
}

export interface InvoiceImportPayload {
  config: {
    templateKey: string;
    userJwt: string;
  };
  data: unknown;
}
